import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { isNil } from 'lodash'
import {
  IconAlertTriangle,
  IconArrowBackUp,
  IconCheck,
  IconExternalLink,
  IconLink,
  IconPencil,
  IconPinned,
} from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { toast } from '@/components/toasts/toast.helpers'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'
import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
import {
  editChangeRequestMessage,
  reopenChangeRequest,
  resolveChangeRequest,
} from '@/reducers/voiceAgents/voiceAgents.actions'
import CommentComposer from '@/components/comments/CommentComposer'

import {
  buildChangeRequestSearch,
  buildTranscriptCommentCallLink,
  formatRequestDate,
  formatTurnTimestamp,
  getCommentTypeLabel,
  isOpenRequest,
  isUnassignedTranscript,
} from '../changeRequestHelpers'

import LinkToNodeModal from './LinkToNodeModal'
import ResolveCommentModal from './ResolveCommentModal'
import './ChangeRequestCard.scss'

// =============================================================================
// One change-request ("comment") card. Shared between the drawer Comments
// view and the top-level Change Requests page — the page passes `nodeTitle`
// (target-node pill) and `onClick` (navigate to the request in the call flow);
// the drawer passes `focused` for the deep-link scroll/highlight.
//
// Status lives on the card, not above it: Resolve and Reopen are the one thing
// a reader of a comment does next, so they sit in the card body as a button and
// are mirrored in the kebab for the people who go there first (AVR-879) — except
// where the surface opts out via hideStatusActions.
//
// Edit is the author's own correction and lives only in the kebab (AVR-877). It
// is listed on every comment you wrote, on every surface that renders a card,
// and greys out once the window shuts rather than disappearing — a reader who
// goes looking for it after the fact should find out that it existed and has
// expired, not be left wondering whether the feature is there at all.
// =============================================================================

/**
 * @param {object} props.request raw BE change-request item
 * @param {string} [props.nodeTitle] target-node pill label (page context only)
 * @param {boolean} [props.focused] scroll into view + highlight (drawer deep link)
 * @param {Function} [props.onClick] whole-card click (page navigation)
 * @param {Function} [props.onResolve] resolve handler, called with the (possibly
 *   empty) resolution note — defaults to the agent-scoped resolveChangeRequest;
 *   the call-scoped Transcript Comments tab passes its own.
 * @param {Function} [props.onReopen] reopen handler — same defaulting as onResolve.
 * @param {boolean} [props.orphaned] the highlighted snippet no longer exists in the current text
 * @param {boolean} [props.showCallLink] render the transcript comment's call id,
 *   turn timestamp and "Open call" link — the Studio surfaces, where the call is
 *   somewhere else. The Call Explorer's own tab leaves it off: the reader is
 *   already on that call.
 * @param {number} [props.callId] the call being viewed, when the card renders on one —
 *   routes a node link and a message edit back into the call-keyed slice.
 * @param {boolean} [props.hideStatusActions] drop Resolve/Reopen entirely — both
 *   the card button and the kebab entry. The Call Explorer sets it: a call page
 *   is where comments are read and written, not where the PE queue is worked.
 */
// Mirrors EDIT_WINDOW in the backend router, which is the authority — this is
// only the copy for the greyed-out state.
const EDIT_EXPIRED_TOOLTIP = 'A comment can only be edited for 2 minutes after posting it'

const ChangeRequestCard = ({
  request,
  nodeTitle,
  focused,
  onClick,
  onResolve,
  onReopen,
  orphaned,
  showCallLink,
  callId,
  hideStatusActions,
}) => {
  const dispatch = useDispatch()
  const cardRef = useRef(null)
  const [resolving, setResolving] = useState(false)
  const [linking, setLinking] = useState(false)
  const [editing, setEditing] = useState(false)
  const busy = useSelector((state) => state.voiceAgents.loading.resolveChangeRequest)
  const editBusy = useSelector((state) => state.voiceAgents.loading.editChangeRequest)
  const resolve = onResolve || ((note) => dispatch(resolveChangeRequest(request.id, note)))
  const reopen = onReopen || (() => dispatch(reopenChangeRequest(request.id)))
  // Closing a comment is Agent Studio's queue work, so its editors can do it —
  // a customer's own editors included — plus Balto (org 1). Mirrors the BE's
  // `_has_studio`, which reads the home org (own_organization_id) so a Balto
  // user reviewing another org still resolves. This hides the actions; the BE
  // PATCH is the real boundary.
  const canResolve = useSelector(
    (state) => state.currentUser.own_organization_id === 1 || Boolean(state.currentUser.togo_editor)
  )
  const open = isOpenRequest(request)
  const unassigned = isUnassignedTranscript(request)
  // The author's short rewrite window (AVR-877). The server reports it as a
  // duration rather than a deadline — the stored timestamps carry no timezone,
  // so an absolute time would need the browser to agree with the server about
  // what time it is — and the action layer turns that into `editExpiresAt` on
  // the browser's own clock as each response lands. Reading the deadline rather
  // than the duration is what makes this survive a remount: switching the
  // Open/Resolved filter would otherwise re-arm a full window from the cached
  // duration and offer an Edit the server has already stopped accepting.
  const remainingMs = () => (isNil(request.editExpiresAt) ? 0 : request.editExpiresAt - Date.now())
  const [editWindowOpen, setEditWindowOpen] = useState(
    () => Boolean(request.canEdit) && remainingMs() > 0
  )
  useEffect(() => {
    const left = remainingMs()
    const editable = Boolean(request.canEdit) && left > 0
    setEditWindowOpen(editable)
    if (!editable) {
      return undefined
    }
    // One shot, so the affordance goes on its own instead of surviving until
    // the next refetch and then failing the PATCH in the user's face.
    const timer = setTimeout(() => setEditWindowOpen(false), left)
    return () => clearTimeout(timer)
  }, [request.canEdit, request.editExpiresAt])

  // Closing the window with the box open would strand text the save can no
  // longer accept — drop out of edit mode so the card reads as it will persist.
  useEffect(() => {
    if (!editWindowOpen) {
      setEditing(false)
    }
  }, [editWindowOpen])
  // Linking needs the agent's node list, which only the Studio surfaces have
  // loaded. On the Call Explorer the chip still shows — the status is worth
  // knowing there — but the action that cannot work is not offered.
  const nodesLoaded = useSelector(
    (state) => (getSelectedAgent(state.voiceAgents)?.nodes || []).length > 0
  )
  const turnTimestamp = formatTurnTimestamp(request.turn?.startMs)

  useEffect(() => {
    // Fires on both focus transitions; the guard scrolls only on gaining focus.
    if (focused && cardRef.current) {
      cardRef.current.scrollIntoView({ block: 'nearest' })
    }
  }, [focused])

  const handleCopyLink = () => {
    const link = `${window.location.origin}${window.location.pathname}?${buildChangeRequestSearch(request)}`
    navigator.clipboard
      .writeText(link)
      .then(() => toast.success('Link copied to clipboard'))
      .catch(() => toast.error('Failed to copy link'))
  }

  const handleResolveConfirm = async (note) => {
    const result = await resolve(note)
    // Leave the modal up on failure — the note the user typed is still in it,
    // and the action toasts its own error.
    if (result !== null) {
      setResolving(false)
    }
  }

  const handleEditSubmit = async (message) => {
    const result = await dispatch(editChangeRequestMessage(request.id, message, callId))
    // Keep the box up on failure so the rewrite isn't lost; the action toasts.
    if (result !== null) {
      setEditing(false)
    }
  }

  const statusAction = open
    ? {
        label: 'Resolve comment',
        icon: <IconCheck size={14} />,
        testId: `change-request-resolve-${request.id}`,
        onClick: () => setResolving(true),
      }
    : {
        label: 'Reopen comment',
        icon: <IconArrowBackUp size={14} />,
        testId: `change-request-reopen-${request.id}`,
        onClick: reopen,
      }

  // Resolving is the prompt engineers' queue work; a surface can opt out of it
  // entirely (the Call Explorer does) on top of the org gate. It also steps
  // aside while the box is open — closing a comment out from under an unsaved
  // rewrite would discard the rewrite, and the card has one job at a time.
  const showStatusActions = canResolve && !hideStatusActions && !editing

  const actions = [
    ...(showStatusActions ? [statusAction] : []),
    // Listed on anything you wrote, enabled only while the window is open. The
    // box being open already is the other reason it goes grey — a second click
    // would do nothing, so it says so rather than looking broken.
    ...(request.isAuthor
      ? [
          {
            label: 'Edit comment',
            icon: <IconPencil size={14} />,
            testId: `change-request-edit-${request.id}`,
            disabled: !editWindowOpen || editing,
            disabledTooltip: editing
              ? 'This comment is open for editing below'
              : EDIT_EXPIRED_TOOLTIP,
            onClick: () => setEditing(true),
          },
        ]
      : []),
    // Placing a comment is curation, not closing it, so it is not behind the
    // resolve permission — anyone who can open the agent can tidy its feedback.
    // Studio only, keyed on the absence of a callId rather than on whether any
    // nodes happen to be cached: the selected agent survives navigation, so on
    // a call page the picker would offer the last Studio agent's nodes and
    // submit ids belonging to a different agent entirely.
    ...(unassigned && isNil(callId) && nodesLoaded
      ? [
          {
            label: 'Link to node',
            icon: <IconPinned size={14} />,
            testId: `change-request-link-node-${request.id}`,
            onClick: () => setLinking(true),
          },
        ]
      : []),
    {
      label: 'Copy link to comment',
      icon: <IconLink size={14} />,
      testId: `change-request-copy-link-${request.id}`,
      onClick: handleCopyLink,
    },
  ]

  const clickableProps = onClick
    ? {
        onClick,
        role: 'button',
        tabIndex: 0,
        onKeyDown: (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onClick()
          }
        },
      }
    : {}

  // Anything interactive inside a navigating card has to stop the bubble, or
  // clicking it also fires the card's own onClick.
  const stopCardClick = {
    role: 'presentation',
    onClick: (event) => event.stopPropagation(),
    onKeyDown: (event) => event.stopPropagation(),
  }

  return (
    <div
      ref={cardRef}
      className={classNames('voice-agents__change-request-card', {
        'voice-agents__change-request-card--focused': focused,
        'voice-agents__change-request-card--clickable': Boolean(onClick),
      })}
      data-testid={`change-request-card-${request.id}`}
      {...clickableProps}
    >
      <div className="voice-agents__change-request-card-header">
        <div className="voice-agents__change-request-card-author">
          <Pill xs outlined dataTestId={`change-request-type-${request.id}`}>
            {getCommentTypeLabel(request)}
          </Pill>
          <span className="voice-agents__change-request-card-author-name">
            {/* POST/PATCH responses carry no createdByName (only the GET
                routes resolve names) — fall back to the id until a refetch. */}
            {request.createdByName || request.createdBy}
          </span>
        </div>
        <div className="voice-agents__change-request-card-meta">
          <span className="voice-agents__change-request-card-date">
            {formatRequestDate(request.createdAt)}
          </span>
          {/* Keep kebab clicks from triggering the whole-card navigation */}
          <span className="voice-agents__change-request-card-actions" {...stopCardClick}>
            <ActionsDropdown actions={actions} dataTestId={`change-request-menu-${request.id}`} />
          </span>
        </div>
      </div>
      {/* Inline/transcript comments quote the span they anchor to so the card
          reads on its own, away from the highlighted source text. */}
      {request.highlight?.snippet && (
        <p
          className={classNames('voice-agents__change-request-card-snippet', {
            'voice-agents__change-request-card-snippet--orphaned': orphaned,
          })}
          data-testid={`change-request-snippet-${request.id}`}
        >
          {request.highlight.snippet}
        </p>
      )}
      {orphaned && (
        <span
          className="voice-agents__change-request-card-orphaned"
          data-testid={`change-request-orphaned-${request.id}`}
        >
          <IconAlertTriangle size={14} />
          Text no longer in this prompt
        </span>
      )}
      {editing ? (
        <span className="voice-agents__change-request-card-edit" {...stopCardClick}>
          <CommentComposer
            initialValue={request.message}
            onSubmit={handleEditSubmit}
            onCancel={() => setEditing(false)}
            busy={editBusy}
            submitLabel="Save"
            busyLabel="Saving…"
            placeholder="Edit your comment…"
            ariaLabel="Edit comment"
            dataTestId={`change-request-edit-composer-${request.id}`}
          />
        </span>
      ) : (
        <p className="voice-agents__change-request-card-message">{request.message}</p>
      )}
      {nodeTitle && (
        <Pill xs outlined dataTestId={`change-request-node-${request.id}`}>
          {nodeTitle}
        </Pill>
      )}
      {unassigned && (
        <Pill xs warning dataTestId={`change-request-unassigned-${request.id}`}>
          Unassigned
        </Pill>
      )}
      {/* Which call and where in it — the context a comment loses once it is
          read next to a prompt instead of next to the transcript (AVR-878). */}
      {showCallLink && request.call?.id && (
        <div className="voice-agents__change-request-card-call" {...stopCardClick}>
          <span data-testid={`change-request-call-${request.id}`}>
            Call {request.call.id}
            {turnTimestamp && ` · ${turnTimestamp}`}
          </span>
          <a
            className="voice-agents__change-request-card-call-link"
            href={buildTranscriptCommentCallLink(request)}
            data-testid={`change-request-open-call-${request.id}`}
          >
            <IconExternalLink size={14} />
            Open call
          </a>
        </div>
      )}
      {!open && (request.resolvedByName || request.resolvedBy) && (
        <div
          className="voice-agents__change-request-card-resolved-by"
          data-testid={`change-request-resolved-by-${request.id}`}
        >
          <IconCheck size={14} />
          Resolved by {request.resolvedByName || request.resolvedBy}
          {request.resolvedAt && <> · {formatRequestDate(request.resolvedAt)}</>}
        </div>
      )}
      {!open && request.resolutionNote && (
        <p
          className="voice-agents__change-request-card-resolution-note"
          data-testid={`change-request-resolution-note-${request.id}`}
        >
          {request.resolutionNote}
        </p>
      )}
      {showStatusActions && (
        <div className="voice-agents__change-request-card-status-action" {...stopCardClick}>
          {open ? (
            <Button
              small
              onClick={() => setResolving(true)}
              data-testid={`change-request-resolve-button-${request.id}`}
            >
              <IconCheck size={14} />
              Resolve
            </Button>
          ) : (
            <Button
              small
              onClick={reopen}
              data-testid={`change-request-reopen-button-${request.id}`}
            >
              <IconArrowBackUp size={14} />
              Reopen
            </Button>
          )}
        </div>
      )}
      {/* The modal renders through a portal, but React events bubble up the
          component tree, not the DOM one — without this, typing in the note
          (Enter/Space) or clicking Resolve would also fire the card's own
          navigation handlers. */}
      <span {...stopCardClick}>
        <ResolveCommentModal
          show={resolving}
          busy={busy}
          onClose={() => setResolving(false)}
          onConfirm={handleResolveConfirm}
        />
        <LinkToNodeModal
          show={linking}
          request={request}
          callId={callId}
          onClose={() => setLinking(false)}
        />
      </span>
    </div>
  )
}

export default ChangeRequestCard
